// src/pages/FartChampionshipPage.jsx
import { Box, Card, Container, Typography } from "@mui/material";
import { AnimatedBackground } from "components/AnimatedBackground";
import Iconify from "components/iconify";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import { useEffect, useState } from "react";

// Dummy images (replace with your own)
import CenteredFooter from "examples/Footers/CenteredFooter";

import { useWallet } from "@solana/wallet-adapter-react";
import borders from "assets/theme/base/borders";
import { CartoonCardAvatar } from "components/CartoonCard";
import CartoonDialog from "components/CartoonDialog.js/CartoonDialog";
import LoadingOverlay from "components/LoadingOverlay";
import { useBoolean } from "hooks/useBoolean";
import { useFartlabProgram } from "hooks/useFartlabProgram";
import { fetchFartSubmissions, fetchVotes, getCurrentChampionship, getFlartPurchaseCost, submitVote } from "utils/anchor/oracleConnector";
import { fetchLatestChampionship } from "utils/solana/championship";
import { getCurrentPrizePool } from "utils/solana/prices";
import Faq from "./sections/Faq";
import PrizesBreakdown from "./sections/PrizesBreakdown";
import Submissions from "./sections/Submissions";
import { PublicKey } from "@solana/web3.js";
import { transferSplToken } from "utils/solanaTransfers";
import { formatSol, formatTokenAmount } from "utils/tokenUtils";


export const SUBMIT_BASE_PRICE = 0.2;
export const VOTE_BASE_PRICE = 0.05;

function Countdown({ label, target }) {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(target));

  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(getTimeLeft(target)), 1000);
    return () => clearInterval(interval);
  }, [target]);

  function getTimeLeft(target) {
    const nowInSeconds = Math.floor(Date.now() / 1000);
    const diff = target - nowInSeconds;

    const seconds = diff % 60;
    const minutes = Math.floor(diff / 60) % 60;
    const hours = Math.floor(diff / 3600);

    return { hours, minutes, seconds };
  }

  return (
    <>
      <Typography variant="h3" sx={{ color: "#fff", mt: 4 }}>
        {label}
      </Typography>
      <Typography variant="h1" sx={{ color: "#fff", mt: 4 }}>
        {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
      </Typography>
    </>
  );
}

function FartChampionshipPage() {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleScroll = (id) => {
    const nextSection = document.getElementById(id);
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  };
  const [championship, setChampionship] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [totalPrizePool, setTotalPrizePool] = useState(0);
  const [userStats, setUserStats] = useState(null);
  // const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBeforeStart, setIsBeforeStart] = useState(false);
  const [isChampionshipCompleted, setIsChampionshipCompleted] = useState(false);
  const [isChampionshipOngoing, setIsChampionshipOngoing] = useState(false);
  const [championshipDuration, setChampionshipDuration] = useState(0);
  const [isReadOnlyProgram, setIsReadOnlyProgram] = useState(false);
  // const isBeforeStart = CHAMPIONSHIP.isActive && now < CHAMPIONSHIP.startTime;

  const { publicKey: walletAddress, connected, sendTransaction, signTransaction } = useWallet();
  const { program, provider, wallet, connection } = useFartlabProgram();

  const showDialog = useBoolean();
  const [dialogData, setDialogData] = useState({});

  const disableChampionshipLoad = true;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch read only datea
        // setIsReadOnlyProgram(isReadOnly);
        // setProgram(program);
        if (disableChampionshipLoad) {
          setLoading(false);
          return;
        }

        const championship = await fetchLatestChampionship(program, provider);
        if (!championship) {
          setLoading(false);
          setChampionship(null);
          setSubmissions([]);
          setTotalPrizePool(0);
          setUserStats(null);
          setIsBeforeStart(false);
          setIsChampionshipCompleted(false);
          setIsChampionshipOngoing(false);
          return;
        }
        const nowInSeconds = Math.floor(Date.now() / 1000); // JS ms → seconds
        const durationSeconds = championship.endTime.toNumber() - championship.startTime.toNumber();
        const durationHours = Math.floor(durationSeconds / 3600);
        setChampionshipDuration(durationHours);
        setIsBeforeStart(championship.startTime.toNumber() > nowInSeconds);
        setIsChampionshipCompleted(championship.endTime.toNumber() < nowInSeconds);
        setIsChampionshipOngoing(
          championship.startTime.toNumber() < nowInSeconds &&
            championship.endTime.toNumber() > nowInSeconds
        );
        setChampionship(championship);

        const championshipUuid = championship.uuid;
        const fartSubmissions = await fetchFartSubmissions({ championship_uuid: championshipUuid });
        const { submissionsWithVotes, totalPrizePool, userStats } = await getCurrentPrizePool(
          program,
          provider,
          championshipUuid,
          fartSubmissions
        );
        setTotalPrizePool(totalPrizePool);
        setUserStats(userStats);
        // const submissionsWithVotes = await fetchSubmissionsWithVotes(
        //   program,
        //   provider,
        //   championshipUuid,
        //   walletKp.publicKey
        // );

        if (submissionsWithVotes && submissionsWithVotes.length > 0) {
          setSubmissions(submissionsWithVotes);
        } else {
          setSubmissions([]);
        }
      } catch (error) {
        console.error("❌ Error loading championship:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const showDialogFn = ({
    title,
    description,
    onAccept,
    acceptLabel = "Accept",
    cancelLabel = "Cancel",
  }) => {
    setDialogData({
      title,
      description,
      onAccept,
      acceptLabel,
      cancelLabel,
    });
    showDialog.onTrue();
  };

  const onSubmitVote = async (fart) => {
    if (!connected || !walletAddress) {
      console.error("Wallet not connected");
      return;
    }

    try {
      setLoading(true);
      // 1. Verify championship exists
      if (!championship) {
        const { championship: latestChamp } = await getCurrentChampionship();
        if (!latestChamp) throw new Error("No active championship found");
        setChampionship(latestChamp);
      }

      // 2. Check existing votes
      const [votes, costInfo] = await Promise.all([
        fetchVotes({
          championship_uuid: championship.uuid,
          voter_public_key: walletAddress.toString(),
        }),
        getFlartPurchaseCost("vote"),
      ]);

      console.log("Votes:", votes);
      console.log("Cost Info:", costInfo);
      if (votes.data.length > 0 || !costInfo) {
        throw new Error("You've already voted in this championship!");
      }

      const costTokenRaw = costInfo.costTokenRaw;
      const costTokenFormatted = formatTokenAmount(costTokenRaw);
      const costSol = formatSol(costInfo.solAmount);
      showDialogFn({
        title: "Ready to vote? 💥",
        description: `Voting costs ${costTokenFormatted} $FLART (~${costSol} SOL).\n\nYour vote counts — if your pick wins, you earn FLART too!`,
        acceptLabel: "Vote Now 🗳️",
        cancelLabel: "Cancel",
        onAccept: async () => {
          try {
            const rewardsPool = new PublicKey(process.env.REACT_APP_REWARDS_POOL);
            const mintAddress = new PublicKey(process.env.REACT_APP_TOKEN_MINT);

            const { txId } = await transferSplToken({
              senderPublicKey: walletAddress,
              recipientAta: rewardsPool,
              mintPublicKey: mintAddress,
              amount: BigInt(costInfo.requiredFlart),
              connection,
              sendTransaction,
              signTransaction,
            });
            console.log("Transaction ID:", txId);
            const { vote: submittedVote } = await submitVote({
              vote: {
                fartSubmissionUuid: fart.uuid,
                championshipUuid: championship.uuid,
                voter: walletAddress.toString(),
              },
              txId: txId,
              senderWallet: walletAddress.toString(),
            });

            setSubmissions((prev) =>
              prev.map((sub) =>
                sub.uuid === fart.uuid
                  ? {
                      ...sub,
                      voteCount: sub.voteCount + 1,
                      votes: [...(sub.votes || []), submittedVote],
                    }
                  : sub
              )
            );
          } catch (err) {
            console.error("Vote error inside dialog:", err);
            alert(err.message);
          }
        },
      });
    } catch (error) {
      console.error("Vote failed:", error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <DefaultNavbar
        routes={[]}
        // action={{
        //   type: "phantom",
        //   walletAddress,
        // }}
        transparent
        light
        // sticky
      />
      <AnimatedBackground>
        <Box
          sx={{
            width: "100%",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Container sx={{ pt: 0, textAlign: "center" }}>
            <Typography
              variant="h2"
              sx={{
                color: "#FFD700",
                // fontWeight: "bold",
                // textShadow: "3px 3px 0 rgba(0,0,0,0.4)",
                mb: 3,
              }}
            >
              🏆 Fart Championship Arena
            </Typography>

            {championship ? (
              <>
                {isBeforeStart ? (
                  <Countdown label={"💣 Starting in"} target={championship.startTime.toNumber()} />
                ) : (
                  <>
                    {isChampionshipOngoing && (
                      <Countdown label={"🧨 Ending in"} target={championship.endTime.toNumber()} />
                    )}
                    <Typography
                      variant="h1"
                      sx={{
                        color: "#22C55E",
                        mt: 4,
                      }}
                    >
                      💰 Reward Pool: {totalPrizePool} $FLART
                    </Typography>
                    {/* Breakdown */}
                    <Typography variant="h5" sx={{ mt: 2, color: "#fff" }}>
                      💨 {submissions.length} Submissions • ✅{" "}
                      {submissions.reduce((acc, f) => acc + f.voteCount, 0)} Votes
                    </Typography>
                    {isChampionshipCompleted && (
                      <Box sx={{ mt: 4 }}>
                        <Box
                          onClick={() => handleScroll("prize-breakdown")}
                          sx={{
                            display: "inline-block",
                            px: 3,
                            py: 1.5,
                            borderRadius: borders.borderRadius.xl,
                            backgroundColor: "#FFB300",
                            border: "3px solid black",
                            boxShadow: "4px 4px 0 rgba(0,0,0,0.3)",
                            cursor: "pointer",
                            fontWeight: "bold",
                            color: "#000",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "6px 6px 0 rgba(0,0,0,0.4)",
                            },
                          }}
                        >
                          🎁 View Full Prize Distribution
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </>
            ) : (
              <Typography variant="h4" sx={{ mt: 4, color: "#fff" }}>
                😢 No active championship right now. Come back soon!
              </Typography>
            )}

            {/* Rules Section */}
            {!isChampionshipCompleted && (
              <Card
                sx={{
                  mt: 6,
                  mb: 4,
                  backgroundColor: "#fff9c4",
                  border: "4px dashed #FFD700",
                  borderRadius: borders.borderRadius.xl,
                  p: 3,
                  maxWidth: "700px",
                  mx: "auto",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold", mb: 2, textShadow: "1px 1px white" }}
                >
                  📜 Championship Rules
                </Typography>
                <Typography>🔥 Up to 5 fart submissions per user</Typography>
                <Typography>
                  💵 Submission Fee: {SUBMIT_BASE_PRICE.toFixed(2)} SOL (in $FLART)
                </Typography>
                <Typography>🗳️ Vote Fee: {VOTE_BASE_PRICE.toFixed(2)} SOL (in $FLART)</Typography>
                <Typography>⏰ Duration: {championshipDuration || 48}h</Typography>
                <Typography>🏆 Top farts and top voters win prizes!</Typography>
              </Card>
            )}

            {isChampionshipCompleted && (
              <>
                {/* Submissions List */}
                <Typography variant="h4" sx={{ color: "#FFD700", mt: 6, fontWeight: "bold" }}>
                  Leaderboard 💨
                </Typography>

                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: 3,
                  }}
                >
                  {submissions.map((fart) => (
                    <CartoonCardAvatar
                      key={fart.id}
                      name={fart.name}
                      role={"by " + fart.creatorId}
                      trimRole={true}
                      description={fart.description}
                      avatar={fart.image}
                      backgroundColor={fart.bg_color}
                      tilt={true}
                      baseTileAngle={0}
                      endTiltAngle={-3}
                      rank={fart.id}
                      votes={42}
                      isSmall={true}
                      onVote={async () => {
                        await onSubmitVote(fart);
                      }}
                      onPlay={() => {
                        const audio = new Audio(fart.audio_path);
                        audio.play();
                      }}
                      onShare={() => console.log("🔗 shared")}
                      disabledActions={{ vote: isChampionshipCompleted }}
                      sx={{
                        width: 250,
                        p: 2,
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Container>
          {/* Scroll Down Button */}
          <Box
            onClick={() => handleScroll("second-section")}
            sx={{
              position: "absolute",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              cursor: "pointer",
              padding: "12px",
              borderRadius: "50%",
              backgroundColor: "#FFD700",
              border: "3px solid black",
              boxShadow: "6px 6px 0px rgba(0,0,0,0.4)",
              transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
              animation: "bounce 1.5s infinite ease-in-out",

              "&:hover": {
                transform: "translateX(-50%) scale(1.1)",
                boxShadow: "8px 8px 0px rgba(0,0,0,0.4)",
              },

              "&:active": {
                transform: "translateX(-50%) scale(0.95)",
                boxShadow: "4px 4px 0px rgba(0,0,0,0.4)",
              },
            }}
          >
            <Iconify icon="mdi:arrow-down-bold" width={36} height={36} />
          </Box>
        </Box>
        <Box id="second-section" />
        {isChampionshipCompleted && (
          <PrizesBreakdown id={"prize-breakdown"} rewardPool={totalPrizePool} />
        )}

        {/* Submissions Section */}
        {(isChampionshipOngoing || isChampionshipCompleted) && (
          <Submissions
            id={"submissions"}
            submissions={submissions}
            isChampionshipCompleted={isChampionshipCompleted}
            onSubmitVote={onSubmitVote}
          />
        )}
        {/* FAQ Section */}
        <Faq id={"faq"} />
        <CenteredFooter light />
        <LoadingOverlay show={loading} />
        <CartoonDialog
          open={showDialog.value}
          onClose={() => showDialog.onFalse()}
          title={dialogData?.title}
          description={dialogData?.description}
          onAccept={() => {
            if (dialogData && dialogData.onAccept) {
              dialogData.onAccept();
            }
            showDialog.onFalse();
          }}
          onCancel={() => showDialog.onFalse()}
          acceptLabel={
            dialogData && dialogData.acceptLabel ? dialogData.acceptLabel : "Let it rip 💥"
          }
          cancelLabel={dialogData && dialogData.cancelLabel ? dialogData.cancelLabel : "Cancel"}
        />
      </AnimatedBackground>
    </>
  );
}

export default FartChampionshipPage;
